// Copyright (c) 2020 WiseTime. All rights reserved.

import React, { useEffect, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { t } from "@lingui/macro"
import { darken } from "polished"
import styled, { css } from "styled-components/macro"
import { TagSuggested, TextEllipsis } from "features/shared/styles"
import {
  AttachTagRequest,
  DeclineTagSuggestionRequest,
  IdList,
  TagSuggestion,
} from "generated/wisetime/user/timeline/v4/timeline_pb"
import SvgIcon from "features/shared/SvgIcon"
import * as TagsSlice from "features/tags/slice"
import { SUGGESTED_TAG_OVERLAY_CLASSNAME } from "features/shared/classNames"
import { RootState } from "app/store"
import * as TagsActions from "../../tags/actions"
import { GetTagRequest } from "../../../generated/legebill/site/tag/v4/tag_pb"
import { getTagDescription } from "../../tags/tags"
import { TagRef } from "../../../generated/wisetime/user/type/tag_ref_pb"
import * as TimelineActions from "../actions"
import * as TimelineAdditionalUiSlice from "../slices/additionalUi"
import * as UserContextSlice from "../../user-context/slice"
import { selectTagColor } from "../slices/timeline"
import { HexColor } from "../color"
import { renderLabel, StyledWarningIconWrapper } from "./TagLabel"

const Overlay = styled.div<{ tagColor: HexColor }>`
  position: absolute;
  left: 0;
  top: 1px;
  bottom: 1px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  border-radius: 2px 0 0 2px;
  transform: translate3d(-100%, 0, 0);
  background-color: ${({ theme }): string => theme.colors.white};
  width: 0;
  overflow: hidden;
  z-index: 1;
  border: 1px dashed ${({ tagColor }): string => darken(0.2, tagColor)};
  border-left-width: 0;
  transition: transform .4s ease, width .2s linear;
  
  @media (max-width: ${({ theme }): string => theme.screen.md}) {
    top: -1px;
    bottom: -1px;
  }
`

const Wrapper = styled.div<{ tagColor: HexColor, hasDifferentTagAssignee: boolean }>`
  ${TagSuggested};
  color: ${({ tagColor }): string => darken(0.5, tagColor)};
  border-color: ${({ tagColor }): string => darken(0.3, tagColor)};

  ${({ hasDifferentTagAssignee }) => hasDifferentTagAssignee && css`
    padding-right: 28px;
  `}
`

const Label = styled.span`
  ${TextEllipsis};
  display: block;
`

const TagAcceptIconWrapper = styled.span`
  background-color: ${({ theme }): string => theme.colors.pastelGreen};
  font-size: 1.3rem;
  width: 14px;
  height: 13px;
  text-align: center;
  border-radius: 50%;
  transition: background-color .3s ease;
`

const TagAcceptIcon = styled(SvgIcon)`
  vertical-align: 4px;
  width: 12px;
  min-width: 12px;
  height: 5px;
  color: ${({ theme }): string => theme.colors.white};
`

const TagRejectIcon = styled.div`
  width: 13px;
  height: 13px;
  border-radius: 50%;
  background-color: ${({ theme }): string => theme.colors.sweetPink};
  position: relative;
  transition: background-color .3s ease;
 
  &:after,
  &:before {
    content: '';
    width: 5px;
    height: 1px;
    background-color: ${({ theme }): string => theme.colors.white};
    position: absolute;
    top: 6px;
    left: 4px;
  }
  
  &:after {
    transform: rotate(45deg);
  }

  &:before {
    transform: rotate(-45deg);
  }
`

const OverlayButton = styled.button`
  display: flex;
  align-items: center;
  padding: 1px 2px 2px;
  width: 18px;
  height: 16px;
  line-height: 17px;
  justify-content: flex-start;
  background-color: rgba(131, 226, 123, .3);
  transition: background-color .3s ease;
  
  &:first-child {
    border-radius: 2px 0 0 2px;
  }
  
  &:hover {
    background-color: rgba(131, 226, 123, .7);

    ${TagAcceptIconWrapper} {
      background-color: ${({ theme }): string => theme.colors.apple};
    }
  }
`

const OverlayButtonReject = styled(OverlayButton)`
  background-color: rgba(253, 157, 157, .3);
  
  &:hover {
    background-color: rgba(253, 157, 157, .7);
    ${TagRejectIcon} {
      background-color: ${({ theme }): string => theme.colors.bittersweet};
    }
  }
`

type Props = {
  tagSuggestion: TagSuggestion.AsObject,
  timeRowId: string,
}

const TagSuggestionLabel = ({ tagSuggestion, timeRowId }: Props): React.ReactElement<Props> | null => {
  const dispatch = useDispatch()
  const currentExternalId = useSelector(UserContextSlice.selectCurrentExternalId)
  const userEmail = useSelector(UserContextSlice.selectUserEmail)
  const isInitialBatchLoaded = useSelector(TagsSlice.selectIsInitialBatchLoaded)
  const tagsViewType = useSelector(TimelineAdditionalUiSlice.selectTagsViewType)
  const cachedTag = useSelector(
    (state: RootState) => TagsSlice.selectTagsCache(state)[tagSuggestion.tag?.id ?? ""],
    (left, right) => left?.name === right?.name,
  )
  useEffect(() => {
    if (isInitialBatchLoaded && cachedTag === undefined && tagSuggestion.tag?.id) {
      // If the full tag hasn't been cached, fetch and cache it now
      dispatch(TagsActions.getTag({
        request: new GetTagRequest().setTagId(tagSuggestion.tag?.id),
        swallowFailure: true
      }))
    }
  }, [isInitialBatchLoaded, tagSuggestion])

  const selectColor = useMemo(selectTagColor, [])
  const tagColor = useSelector((state: RootState) => selectColor(state, cachedTag?.id ?? ""))

  if (cachedTag === undefined) {
    return null
  }

  const hasDifferentTagAssignee = cachedTag.assigneeExternalIdsList.length !== 0
    && [userEmail, currentExternalId].every(item => !cachedTag.assigneeExternalIdsList.includes(item))

  const onAcceptTagSuggestion = (): void => {
    const attachTagRequest = new AttachTagRequest().setTagId(cachedTag.id)
    attachTagRequest.setTimeRowIds(new IdList().setIdsList([timeRowId]))
    const tagRef = new TagRef().setId(cachedTag.id)
      .setSiteId(cachedTag.siteId)
      .setName(cachedTag.name)
    dispatch(TimelineActions.attachTag({ attachTagRequest, tag: tagRef }))
  }

  const onDeclineTagSuggestion = (): void => {
    const request = new DeclineTagSuggestionRequest()
    request.setTagSuggestionId(tagSuggestion.id)
    dispatch(TimelineActions.declineTagSuggestion(request))
  }

  return (
    <Wrapper
      tagColor={tagColor}
      title={`${cachedTag.name}\n${getTagDescription(cachedTag)}`}
      hasDifferentTagAssignee={hasDifferentTagAssignee}
      onClick={(event: React.MouseEvent<HTMLElement>): void => event.stopPropagation()}
    >
      <Overlay tagColor={tagColor} className={SUGGESTED_TAG_OVERLAY_CLASSNAME}>
        <OverlayButton
          type="button"
          tabIndex={-1}
          onClick={onAcceptTagSuggestion}
          title={t({ id: "label_accept", message: "Accept" })}
        >
          <TagAcceptIconWrapper>
            <TagAcceptIcon name="checked"/>
          </TagAcceptIconWrapper>
        </OverlayButton>
        <OverlayButtonReject
          type="button"
          tabIndex={-1}
          onClick={onDeclineTagSuggestion}
          title={t({ id: "label_reject", message: "Reject" })}
        >
          <TagRejectIcon/>
        </OverlayButtonReject>
      </Overlay>
      <Label>{renderLabel(tagsViewType, cachedTag)}</Label>
      {hasDifferentTagAssignee && (
        <StyledWarningIconWrapper
          title={t({
            id: "label_you_are_not_assignee_for_tag",
            message: "You are not an assignee for this tag.",
          })}
        >
          <SvgIcon name="exclamation-circle"/>
        </StyledWarningIconWrapper>
      )}
    </Wrapper>
  )
}

export default TagSuggestionLabel
